import React from "react";
import { useState, useRef } from "react"; // 🎯 added useRef
import { Link } from "react-router-dom";
import { Col, Input, Label, Row, Spinner, Button as RsButton } from "reactstrap";
import ImageUploader from "../../../../helpers/Common/ImageUploader";
import { CustomSnackbar } from "../../../../helpers/CustomSnackbar";
import noLogo from "../../../../assets/images/featured-job/no-logo.png";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { scrollWindow } from "../../../../helpers/common";
import AddMoreDetails from "./AddMoreDetails";
import CompanyFormCompletion from "./CompanyFormCompletion";
import LocationSearch from "../../../../helpers/Common/LocationSearch";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import ChangeLoginFieldModal from "../../../../helpers/Common/ChangeLoginFieldModal";

const ManageCompanyForm = ({
  onSubmit,
  companyDetails,
  loading,
  formType = "add",
  compulsoryMode = false,
}) => {
  const [errors, setErrors] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const customer = useSelector((state) => state.customer.profile) || {};
  const imageUploaderRef = useRef(null); // 🎯 ref to trigger image upload on submit

  // 🎯 OTP-based change modals — same pattern as UpdateProfile.jsx.
  // NOTE: this reuses ChangeLoginFieldModal, which verifies/updates the
  // EMPLOYER'S OWN account login email/mobile via OTP — not a separate
  // company-only contact field. Confirm this is the intended behavior.
  const [openChangeEmailModal, setOpenChangeEmailModal] = useState(false);
  const [openChangeMobileModal, setOpenChangeMobileModal] = useState(false);

const getInitialState = (data) => ({
  logo: data?.logo || "",
  logoUrl: data?.logoUrl || "",
  name: data?.name || "",
  companyDetails: data?.companyDetails || "",
  ownerName: data?.ownerName || "",
  totalEmployees: data?.totalEmployees || 0,
  establishmentYear: data?.establishmentYear || 0,
  companyType: data?.companyType || "",
  mapLocation: data?.mapLocation || "",
  contactNumber: data?.contactNumber || "",
  mobileNumber: data?.mobileNumber || customer?.data?.phoneNumber || "",
  emailAddress: data?.emailAddress || customer?.data?.email || "",
  websiteUrl: data?.websiteUrl || "",
  companyAddress: data?.companyAddress || "",
  jobLocationId: data?.jobLocationId || "",
  gstNumber: data?.gstNumber || "",
});

  const [formData, setFormData] = useState(() =>
    getInitialState(companyDetails),
  );

  useEffect(() => {
    if (companyDetails && Object.keys(companyDetails).length > 0) {
      setFormData(getInitialState(companyDetails));
    }
  }, [companyDetails]);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onNumberInputChange = (e) => {
    const { name, value } = e.target;
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }

    if (!value) {
      setFormData((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    if (!/^\d+$/.test(value)) return;

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 🎯 handlers matching UpdateProfile.jsx's pattern
  const handleChangeEmail = () => setOpenChangeEmailModal(true);
  const handleChangeMobile = () => setOpenChangeMobileModal(true);

  const handleEmailChangeSuccess = (newEmail) => {
    setFormData((prev) => ({ ...prev, emailAddress: newEmail }));
    CustomSnackbar.success("Email updated successfully!");
  };

  const handleMobileChangeSuccess = (newMobile) => {
    setFormData((prev) => ({ ...prev, mobileNumber: newMobile }));
    CustomSnackbar.success("Mobile number updated successfully!");
  };

  const validateForm = () => {
    let err = {};

    if (!formData.name?.trim()) {
      CustomSnackbar.error("Please enter your company name");
      err.name = true;
    }

    if (!formData.companyAddress?.trim()) {
      CustomSnackbar.error("Please enter your company address");
      err.companyAddress = true;
    }

    if (!formData.companyDetails?.trim()) {
      CustomSnackbar.error("Please enter your company details");
      err.companyDetails = true;
    }

    // 🎯 only require these in the FULL form (not during compulsory onboarding)
    if (!compulsoryMode) {
      if (!formData.emailAddress?.trim()) {
        err.emailAddress = true;
      }

      if (!formData.mobileNumber) {
        err.mobileNumber = true;
      }

      if (!formData.jobLocationId) {
        CustomSnackbar.error("Please select your company location");
        err.jobLocationId = true;
      }
    }

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleOpenPreview = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowPreviewModal(true);
    }
  };

  // 🎯 THE FIX: now uploads the pending logo (if any) via the ImageUploader
  // ref BEFORE submitting the form, instead of relying on its own Save button.
  // 🎯 THE FIX: company must exist before the logo can be uploaded, so we now
  // create/update the company FIRST, then upload the pending logo AFTER —
  // instead of uploading the logo before submitting the form.
  const onFormSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    setShowPreviewModal(false);

    try {
      // 1. Create/update the company first (without waiting on logo)
      await onSubmit({ ...formData });

      // 2. Only after the company exists, upload the pending logo (if any)
      if (imageUploaderRef.current?.hasPendingImage) {
        await imageUploaderRef.current.saveImage();
      }
    } catch (err) {
      // Errors already surfaced via CustomSnackbar/apiErrorHandler
      // inside onSubmit / saveImage — nothing extra to do here.
    }
  };
  const updateImageHandle = (imageName) => {
    setFormData((prev) => ({ ...prev, logo: imageName }));
  };

  useEffect(() => {
    if (typeof scrollWindow === "function") {
      scrollWindow();
    }
  }, []);

  const logoSrc = formData?.logoUrl
    ? formData.logoUrl
    : formData?.logo
    ? formData.logo.startsWith("http")
      ? formData.logo
      : `https://sdsjobseekers.runasp.net/uploads/company_logo/${formData.logo}`
    : noLogo;

  return (
    <>
      <CompanyFormCompletion formData={formData} />

      <form onSubmit={onFormSubmit} className="job-post-form shadow mt-4">
        <div className="job-post-content box-shadow-md rounded-3 p-4">
          <ImageUploader
            ref={imageUploaderRef}
            uploadPath="/CandidateAttachment/upload-company-logo"
            assetType="company_logo"
            updateImageHandle={updateImageHandle}
            imageName={logoSrc}
            placeholderImage={noLogo}
          />

          <Row className="row">
            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="compName" className="form-label">
                  Company Name *
                </Label>
                <Input
                  type="text"
                  value={formData?.name}
                  className="form-control"
                  autoComplete="off"
                  id="compName"
                  placeholder="Title"
                  name="name"
                  onChange={onChange}
                  invalid={!!errors.name}
                  data-score={10}
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="email" className="form-label">
                  Email Address *
                </Label>
                <div className="d-flex">
                  <Input
                    type="email"
                    className="form-control"
                    autoComplete="off"
                    id="email"
                    placeholder="Email Address"
                    value={formData?.emailAddress}
                    name="emailAddress"
                    invalid={!!errors.emailAddress}
                    data-score={10}
                    disabled
                  />
                  <RsButton
                    color="outline-primary"
                    size="sm"
                    className="profile-edit-btn"
                    onClick={handleChangeEmail}
                    title="Change Email"
                    type="button"
                  >
                    <i className="uil uil-pen"></i>
                  </RsButton>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="mobileNumber" className="form-label">
                  Mobile Number *
                </Label>
                <div className="d-flex">
                  <Input
                    type="text"
                    maxLength={10}
                    className="form-control"
                    autoComplete="off"
                    id="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData?.mobileNumber}
                    name="mobileNumber"
                    invalid={!!errors.mobileNumber}
                    data-score={10}
                    disabled
                  />
                  <RsButton
                    color="outline-primary"
                    size="sm"
                    className="profile-edit-btn"
                    onClick={handleChangeMobile}
                    title="Change Mobile Number"
                    type="button"
                  >
                    <i className="uil uil-pen"></i>
                  </RsButton>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-4">
                <Label htmlFor="jobLocationId" className="form-label">
                  Company Location *
                </Label>
                <LocationSearch
                  location={formData.jobLocationId}
                  setLocation={(id) =>
                    setFormData((prev) => ({
                      ...prev,
                      jobLocationId: id,
                    }))
                  }
                  labelField="state"
                  valueField="id"
                  leftIcon={null}
                />
              </div>
            </Col>

            <AddMoreDetails
              formData={formData}
              setFormData={setFormData}
              onChange={onChange}
              onNumberInputChange={onNumberInputChange}
              errors={errors}
            />

            <Col lg={12}>
              <div className="mb-4">
                <Label htmlFor="compDetails" className="form-label">
                  About Company Details *
                </Label>

                <div
                  className={
                    errors.companyDetails
                      ? "is-invalid border border-danger rounded"
                      : ""
                  }
                >
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData?.companyDetails || ""}
                    config={{
                      placeholder:
                        "The more you share about your company, the more candidate trust you win...",
                      toolbar: [
                        "heading",
                        "|",
                        "bold",
                        "italic",
                        "bulletedList",
                        "numberedList",
                        "blockQuote",
                        "undo",
                        "redo",
                      ],
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      onChange({
                        target: {
                          name: "companyDetails",
                          value: data,
                        },
                      });
                    }}
                  />
                </div>

                {errors.companyDetails && (
                  <div className="invalid-feedback d-block">
                    {errors.companyDetails}
                  </div>
                )}
              </div>
            </Col>

            <Col lg={12}>
              <div className="mb-4">
                <Label htmlFor="compAddress" className="form-label">
                  Company Full Address *
                </Label>
                <Input
                  type="textarea"
                  className="form-control"
                  autoComplete="off"
                  id="compAddress"
                  rows="3"
                  placeholder="Enter Company Address"
                  value={formData?.companyAddress}
                  onChange={onChange}
                  name="companyAddress"
                  invalid={!!errors.companyAddress}
                  data-score={10}
                />
              </div>
            </Col>

            <Col lg={12}>
              <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
                <Link to="/employer/company/manage" className="btn btn-success">
                  Back
                </Link>

                {/* Preview Button */}
                <button
                  type="button"
                  onClick={handleOpenPreview}
                  className="btn btn-info text-white"
                >
                  <i className="mdi mdi-eye me-1"></i> Preview
                </button>

                {loading ? (
                  <button type="button" className="btn btn-primary disabled">
                    {formType === "update" ? "Update Now" : "Create Now"}{" "}
                    <Spinner size="sm" />
                  </button>
                ) : (
                  <button type="submit" className="btn btn-primary">
                    {formType === "update" ? "Update Now" : "Create Now"}{" "}
                    <i className="mdi mdi-send"></i>
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </form>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        toggle={() => setShowPreviewModal(!showPreviewModal)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setShowPreviewModal(false)}>
          Company Profile Preview
        </ModalHeader>
        <ModalBody className="p-4">
          <div className="d-flex align-items-center gap-3 mb-4 border-bottom pb-3">
            <img
              src={logoSrc}
              alt="Company Logo Preview"
              style={{
                width: "80px",
                height: "80px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            />
            <div>
              <h4 className="mb-1">{formData.name || "Company Name"}</h4>
              <p className="text-muted mb-0">
                <i className="mdi mdi-email-outline me-1"></i>
                {formData.emailAddress || "Not provided"} |{" "}
                <i className="mdi mdi-phone-outline me-1"></i>
                {formData.mobileNumber || "Not provided"}
              </p>
            </div>
          </div>

          <Row className="g-3 mb-4">
            <Col md={6}>
              <strong>Company Type:</strong>{" "}
              <span className="text-muted">
                {formData.companyType || "N/A"}
              </span>
            </Col>
            <Col md={6}>
              <strong>Total Employees:</strong>{" "}
              <span className="text-muted">
                {formData.totalEmployees || "N/A"}
              </span>
            </Col>
            <Col md={6}>
              <strong>Establishment Year:</strong>{" "}
              <span className="text-muted">
                {formData.establishmentYear || "N/A"}
              </span>
            </Col>
            <Col md={6}>
              <strong>Website:</strong>{" "}
              <span className="text-muted">{formData.websiteUrl || "N/A"}</span>
            </Col>
            <Col md={6}>
              <strong>GST Number:</strong>{" "}
              <span className="text-muted">{formData.gstNumber || "N/A"}</span>
            </Col>
            <Col md={6}>
              <strong>Owner Name:</strong>{" "}
              <span className="text-muted">{formData.ownerName || "N/A"}</span>
            </Col>
          </Row>

          <div className="mb-4">
            <h6 className="fw-bold border-bottom pb-2">Full Address</h6>
            <p className="text-muted mb-0">
              {formData.companyAddress || "N/A"}
            </p>
          </div>

          <div className="mb-3">
            <h6 className="fw-bold border-bottom pb-2">
              About Company Details
            </h6>
            {formData.companyDetails ? (
              <div
                className="p-3 bg-light rounded"
                dangerouslySetInnerHTML={{ __html: formData.companyDetails }}
              />
            ) : (
              <p className="text-muted">No details provided.</p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowPreviewModal(false)}>
            <i className="mdi mdi-pencil me-1"></i> Back to Edit
          </Button>
          <Button color="primary" onClick={onFormSubmit} disabled={loading}>
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                Confirm & {formType === "update" ? "Update" : "Create"}{" "}
                <i className="mdi mdi-send ms-1"></i>
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <ChangeLoginFieldModal
        openModal={openChangeEmailModal}
        setOpenModal={setOpenChangeEmailModal}
        field="email"
        currentValue={formData.emailAddress}
        onSuccess={handleEmailChangeSuccess}
      />

      <ChangeLoginFieldModal
        openModal={openChangeMobileModal}
        setOpenModal={setOpenChangeMobileModal}
        field="mobile_number"
        currentValue={formData.mobileNumber}
        onSuccess={handleMobileChangeSuccess}
      />
    </>
  );
};

export default ManageCompanyForm;